// ===== K-Tec AI Store =====
// Zustand store for AI conversations, settings, leads, and notifications

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ConversationState, ChatMessage } from './conversationMemory';
import { createConversationState, addMessage, updateConversation } from './conversationMemory';
// ── Types ─────────────────────────────────────────────────────────────────────

export interface AILead {
  id: string;
  conversationId: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  requirement: string;
  estimatedValue?: number;
  language: string;
  source: 'website' | 'admin';
  status: 'new' | 'ai-chatting' | 'lead' | 'quotation-requested' | 'contacted' | 'converted' | 'closed';
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface AISettings {
  publicChatEnabled: boolean;
  voiceInputEnabled: boolean;
  voiceOutputEnabled: boolean;
  sinhalaSupportEnabled: boolean;
  singlishSupportEnabled: boolean;
  productRecommendationsEnabled: boolean;
  packageRecommendationsEnabled: boolean;
  quotationRequestsEnabled: boolean;
  welcomeMessage: string;
  businessInstructions: string;
  contactNumber: string;
  whatsappNumber: string;
  // Knowledge base
  companyDescription: string;
  services: string;
  serviceAreas: string;
  installationPolicy: string;
  warrantyPolicy: string;
  paymentTerms: string;
  supportInfo: string;
  faqs: Array<{ question: string; answer: string }>;
}

export interface AINotification {
  id: string;
  type: 'lead' | 'quotation-request' | 'new-conversation';
  title: string;
  message: string;
  conversationId: string;
  customerName?: string;
  customerPhone?: string;
  requirement?: string;
  estimatedValue?: number;
  isRead: boolean;
  createdAt: string;
}

interface AIStore {
  // Public conversations
  conversations: ConversationState[];
  activeConversationId: string | null;
  
  // Leads
  leads: AILead[];
  
  // Notifications
  aiNotifications: AINotification[];
  
  // Settings
  settings: AISettings;
  
  // Chat widget state
  isChatOpen: boolean;
  
  // Actions
  startNewConversation: () => string;
  getActiveConversation: () => ConversationState | null;
  updateConversationState: (id: string, updates: Partial<ConversationState>) => void;
  addMessageToConversation: (id: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  setActiveChatOpen: (open: boolean) => void;
  
  // Lead actions
  createLead: (lead: Omit<AILead, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateLeadStatus: (id: string, status: AILead['status']) => void;
  getLeadByConversationId: (conversationId: string) => AILead | undefined;
  
  // Notification actions
  addAINotification: (notification: Omit<AINotification, 'id' | 'createdAt' | 'isRead'>) => void;
  markAINotificationRead: (id: string) => void;
  markAllAINotificationsRead: () => void;
  getUnreadAINotificationCount: () => number;
  
  // Settings actions
  updateSettings: (settings: Partial<AISettings>) => void;
}

const DEFAULT_SETTINGS: AISettings = {
  publicChatEnabled: true,
  voiceInputEnabled: true,
  voiceOutputEnabled: true,
  sinhalaSupportEnabled: true,
  singlishSupportEnabled: true,
  productRecommendationsEnabled: true,
  packageRecommendationsEnabled: true,
  quotationRequestsEnabled: true,
  welcomeMessage: `👋 ආයුබෝවන්! මම K-Tec AI Assistant.

CCTV, Alarm Systems, Smart Home, Starlink සහ Networking ගැන ඔයාට උදව් කරන්න පුළුවන්.

සිංහලෙන්, English වලින් හෝ Singlish වලින් මගෙන් අහන්න.`,
  businessInstructions: '',
  contactNumber: '0115 800 800',
  whatsappNumber: '0711 800 800',
  companyDescription: 'K-Tec is Sri Lanka\'s leading security technology company, specializing in CCTV, IP Camera Systems, Alarm Systems, Smart Home Automation, Starlink Internet, and Networking Solutions.',
  services: 'CCTV Systems, IP Camera Systems, Analog Camera Systems, Wi-Fi Cameras, Alarm Systems, Smart Home Automation, Starlink Internet, Networking, Wi-Fi Solutions, PoE Networking, Structured Cabling, NVR and DVR Systems, HDD Storage, Access Control, Installation Services, Maintenance, Warranty and Service Support',
  serviceAreas: 'Islandwide Sri Lanka — Colombo, Kandy, Galle, Matara, Negombo, Kurunegala, Anuradhapura and all major cities',
  installationPolicy: 'Professional installation by K-Tec certified technicians. Free site survey for systems above LKR 50,000.',
  warrantyPolicy: 'Products carry manufacturer warranty (1-3 years). K-Tec provides 1-year installation warranty on all completed projects.',
  paymentTerms: '50% advance, 50% on completion. Cash, bank transfer, card payments accepted.',
  supportInfo: 'Call/WhatsApp: 0115 800 800 | Email: info@ktec.lk | Working hours: 8:30AM - 6:00PM',
  faqs: [
    { question: 'What is the minimum camera system?', answer: 'We recommend minimum 4 cameras for a home, 8 cameras for a shop or office.' },
    { question: 'Do you offer free site survey?', answer: 'Yes, free site survey for projects above LKR 50,000.' },
    { question: 'How long does installation take?', answer: '4-camera system: 1 day, 8-camera system: 1-2 days, larger systems: 2-3 days.' },
  ],
};

const genId = () => `ai-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 5)}`;

export const useAIStore = create<AIStore>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeConversationId: null,
      leads: [],
      aiNotifications: [],
      settings: DEFAULT_SETTINGS,
      isChatOpen: false,

      startNewConversation: () => {
        const conv = createConversationState();
        set(state => ({
          conversations: [...state.conversations, conv],
          activeConversationId: conv.id,
        }));
        return conv.id;
      },

      getActiveConversation: () => {
        const { conversations, activeConversationId } = get();
        if (!activeConversationId) return null;
        return conversations.find(c => c.id === activeConversationId) || null;
      },

      updateConversationState: (id, updates) => {
        set(state => ({
          conversations: state.conversations.map(c =>
            c.id === id ? updateConversation(c, updates) : c
          ),
        }));
      },

      addMessageToConversation: (id, message) => {
        set(state => ({
          conversations: state.conversations.map(c =>
            c.id === id ? addMessage(c, message) : c
          ),
        }));
      },

      setActiveChatOpen: (open) => {
        set({ isChatOpen: open });
        if (open) {
          const { activeConversationId, startNewConversation } = get();
          if (!activeConversationId) {
            startNewConversation();
          }
        }
      },

      createLead: (lead) => {
        const id = genId();
        const now = new Date().toISOString();
        const newLead: AILead = { ...lead, id, createdAt: now, updatedAt: now };
        set(state => ({ leads: [...state.leads, newLead] }));
        
        // Create AI notification
        get().addAINotification({
          type: 'lead',
          title: '🔥 New AI Lead',
          message: `${lead.customerName || 'Website Visitor'} — ${lead.requirement}`,
          conversationId: lead.conversationId,
          customerName: lead.customerName,
          customerPhone: lead.customerPhone,
          requirement: lead.requirement,
          estimatedValue: lead.estimatedValue,
        });
        
        // Also try to push to workflowStore notifications (cross-store)
        try {
          // Dynamic import to avoid circular deps at module level
          import('../admin/store/workflowStore').then(({ useWorkflowStore }) => {
            useWorkflowStore.getState().addNotification({
              type: 'quotation_created',
              title: '🔥 New AI Lead',
              message: `${lead.customerName || 'Website Visitor'} | ${lead.customerPhone || 'No phone'} | ${lead.requirement}`,
            });
          });
        } catch { /* ignore if workflowStore not available */ }
        
        return id;
      },

      updateLeadStatus: (id, status) => {
        set(state => ({
          leads: state.leads.map(l =>
            l.id === id ? { ...l, status, updatedAt: new Date().toISOString() } : l
          ),
        }));
      },

      getLeadByConversationId: (conversationId) => {
        return get().leads.find(l => l.conversationId === conversationId);
      },

      addAINotification: (notification) => {
        const newNote: AINotification = {
          ...notification,
          id: genId(),
          isRead: false,
          createdAt: new Date().toISOString(),
        };
        set(state => ({
          aiNotifications: [newNote, ...state.aiNotifications].slice(0, 100),
        }));
      },

      markAINotificationRead: (id) => {
        set(state => ({
          aiNotifications: state.aiNotifications.map(n =>
            n.id === id ? { ...n, isRead: true } : n
          ),
        }));
      },

      markAllAINotificationsRead: () => {
        set(state => ({
          aiNotifications: state.aiNotifications.map(n => ({ ...n, isRead: true })),
        }));
      },

      getUnreadAINotificationCount: () => {
        return get().aiNotifications.filter(n => !n.isRead).length;
      },

      updateSettings: (settings) => {
        set(state => ({ settings: { ...state.settings, ...settings } }));
      },
    }),
    {
      name: 'ktec-ai-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        conversations: state.conversations.slice(-20), // keep last 20 convos
        leads: state.leads,
        aiNotifications: state.aiNotifications.slice(0, 50),
        settings: state.settings,
      }),
    }
  )
);
